import {
  hasUrlParam,
  useAssociateProject,
  useCoreOrderCart,
  useCreateProject,
  useProject,
  useUpdateProjectConfig,
} from '@customink/project-service-react-components';
import type { FulfillmentMethod } from '@customink/project-service-types';
import {
  FOREIGN_TYPES,
  FULFILLMENT_METHODS,
} from '@customink/project-service-types';
import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';

export const isProjectSettledAtom = atom(false);

export const useIsProjectSettled = () => {
  const [isSettled, setIsSettled] = useAtom(isProjectSettledAtom);
  const {
    data: coreOrder,
    isSuccess: isCoreOrderSuccess,
    isError: isCoreOrderError,
  } = useCoreOrderCart();
  const {
    data: project,
    isSuccess: isProjectLoaded,
    fulfilledTimeStamp: projectFulfilledTimeStamp,
  } = useProject();
  const [updateConfig] = useUpdateProjectConfig();
  const [, { isSuccess: isCreateSuccess }] = useCreateProject();
  const [
    associate,
    {
      isSuccess: isAssocationLoaded,
      status: associationStatus,
      fulfilledTimeStamp: associationFulfilledTimeStamp,
    },
  ] = useAssociateProject();

  // Check if projectId is from URL
  const isUrlProjectId = hasUrlParam('projectId');

  const projectDataCurrentWithAssociation = !!(
    projectFulfilledTimeStamp &&
    associationFulfilledTimeStamp &&
    projectFulfilledTimeStamp >= associationFulfilledTimeStamp
  );

  const coreOrderToken = coreOrder?.token || '';

  const fulfillmentMethod = project?.configuration?.fulfillmentMethod;
  const validMethods: FulfillmentMethod[] = [
    FULFILLMENT_METHODS.BULK,
    FULFILLMENT_METHODS.BSS,
  ];

  const hasValidFulfillmentMethod: boolean =
    !fulfillmentMethod || validMethods.includes(fulfillmentMethod);

  const isProjectLoadedOrCreated: boolean =
    isProjectLoaded && (!!project?.id || isCreateSuccess);
  const isCoreOrderSettled: boolean = isCoreOrderSuccess || isCoreOrderError;
  const isLoaded: boolean =
    isCoreOrderSettled && isProjectLoadedOrCreated && hasValidFulfillmentMethod;

  // Skip association if projectId is from URL
  const requiresAssociation: boolean =
    !isUrlProjectId && isCoreOrderSuccess && !!coreOrderToken;

  useEffect(() => {
    if (isProjectLoaded && !hasValidFulfillmentMethod) {
      updateConfig({ fulfillmentMethod: FULFILLMENT_METHODS.BULK });
    }
  }, [isProjectLoaded, hasValidFulfillmentMethod, updateConfig]);

  // If we have a core order token, we need to associate it with the project.
  useEffect(() => {
    if (
      isLoaded &&
      requiresAssociation &&
      associationStatus === 'uninitialized'
    ) {
      associate({
        foreignKey: coreOrderToken,
        foreignType: FOREIGN_TYPES.CORE_ORDER,
        sync: 'true',
      });
    }
  }, [
    associate,
    associationStatus,
    isLoaded,
    requiresAssociation,
    coreOrderToken,
  ]);

  const shouldBeSettled =
    isLoaded && (!requiresAssociation || projectDataCurrentWithAssociation);

  if (!isSettled && shouldBeSettled) {
    setIsSettled(true);
  }

  return isSettled;
};
